/**
 * Splits text into plain segments and inline mention payloads encoded as JSON
 * objects, e.g.
 * `{"mention":"social_account","handle":"konon_","platform":"tiktok"}` or
 * `{"mention":"video","id":121560,"start_time_sec":null,"end_time_sec":null}`.
 *
 * Brace matching is naive (does not skip `{` / `}` inside JSON strings); that is
 * acceptable for typical handle/platform values.
 */
import type { VideoEvidence, VideoEvidenceInfluencer } from "../ChatEventTypes";
export type MentionPayload = {
	id?: string;
	mention: string;
	handle: string;
	mention_type?: "influencer" | "social_account";
	name?: string;
	bio?: string;
	profile_url?: string;
	profile_picture_url?: string;
	follower_count?: number;
	is_following?: boolean;
	all_follower_counts?: Array<{
		platform: string;
		platform_id?: string;
		follower_count: number;
	}>;
	platform?: string;
	view_count?: number;
	like_count?: number;
	video_count?: number;
};

export type VideoMentionPayload = {
	mention: "video";
	id: string | number;
	start_time_sec?: number | null;
	end_time_sec?: number | null;
	thumbnail_url?: string | null;
	aspect_ratio?: number | null;
	title?: string | null;
	video_id?: string;
	video_url?: string | null;
	/** Full source video duration in seconds. */
	video_duration?: number | null;
	hls_ready?: boolean;
	influencer?: VideoEvidenceInfluencer | null;
	social_account?: VideoEvidence["social_account"] | null;
	view_count?: number | null;
	post_url?: string | null;
};

export type TextOrMentionSegment =
	| { type: "text"; value: string }
	| { type: "mention"; value: MentionPayload }
	| { type: "video_mention"; value: VideoMentionPayload; index: number };

function isAccountMentionPayload(value: unknown): value is MentionPayload {
	if (value === null || typeof value !== "object") {
		return false;
	}
	const o = value as Record<string, unknown>;
	if (typeof o.mention !== "string" || typeof o.handle !== "string") {
		return false;
	}
	if (o.mention === "social_account" && typeof o.platform !== "string") {
		return false;
	}
	return true;
}

function isVideoMentionPayload(value: unknown): value is VideoMentionPayload {
	if (value === null || typeof value !== "object") {
		return false;
	}
	const o = value as Record<string, unknown>;
	return (
		o.mention === "video" &&
		(typeof o.id === "string" || typeof o.id === "number")
	);
}

export function parseMentionJsonInText(text: string): TextOrMentionSegment[] {
	const segments: TextOrMentionSegment[] = [];
	let i = 0;
	let videoIndex = 1;
	const marker = /\{\s*"mention"\s*:/g;

	while (i < text.length) {
		marker.lastIndex = i;
		const m = marker.exec(text);
		if (!m) {
			if (i < text.length) {
				segments.push({ type: "text", value: text.slice(i) });
			}
			break;
		}

		const start = m.index;
		if (start > i) {
			segments.push({ type: "text", value: text.slice(i, start) });
		}

		let depth = 0;
		let j = start;
		for (; j < text.length; j++) {
			const c = text[j];
			if (c === "{") {
				depth++;
			} else if (c === "}") {
				depth--;
				if (depth === 0) {
					j++;
					break;
				}
			}
		}

		const jsonStr = text.slice(start, j);
		try {
			const parsed: unknown = JSON.parse(jsonStr);
			if (isVideoMentionPayload(parsed)) {
				segments.push({
					type: "video_mention",
					value: parsed,
					index: videoIndex++,
				});
			} else if (isAccountMentionPayload(parsed)) {
				segments.push({ type: "mention", value: parsed });
			} else {
				segments.push({ type: "text", value: jsonStr });
			}
		} catch {
			segments.push({ type: "text", value: jsonStr });
		}
		i = j;
	}

	return segments;
}
